import { Row, Col, Form, Input, Label, Button, FormFeedback } from 'reactstrap'
import { useEffect, useState } from 'react'
import Joi from 'joi'
import { useForm, useWatch } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { useDispatch, useSelector } from 'react-redux'
import ManualUploadsCollectionIds from './ManualUploadsComponents/ManualUploadsCollectionIds'
import './ManualUploads.css'
import ManualUploadsSector from './ManualUploadsComponents/ManualUploadsSector'
import ManualUploadsDateTime from './ManualUploadsComponents/ManualUploadsDateTime'
import { postManualUploads } from '../../redux/slices/manualUploadsSlice'
import { DefaultState } from '../../types'

const defaultJoiSchema = {
  startdate: Joi.date().iso().allow('').optional(),
  enddate: Joi.date()
    .iso()
    .min(Joi.ref('startdate'))
    .allow('')
    .optional()
    .messages({
      'date.min': `End Date should be later than Start Date`
    }),
  startsector: Joi.number().integer().min(0).optional().messages({
    'number.base': `Start Sector should be a type of 'number'`,
    'number.min': `Should have a minimum value of 0`
  }),
  endsector: Joi.number()
    .integer()
    .min(Joi.ref('startsector'))
    .optional()
    .messages({
      'number.base': `Start Sector should be a type of 'number'`,
      'number.min': `Should be higher than the Start Sector value`
    }),
  collectionIds: Joi.array().min(1).optional().messages({
    'array.min': `Must select at least one option`
  })
}

const schema = Joi.object(defaultJoiSchema)

const ManualUploadsUserForm = () => {
  const dispatch = useDispatch()
  const searchTypes = [
    { name: 'Date/Time', value: 'time' },
    { name: 'Sector', value: 'sector' }
  ]
  const [type, setType] = useState<'time' | 'sector'>('time')
  const [disable, setDisable] = useState(false)
  const [visible, setVisible] = useState(true)
  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    register
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema)
  })
  const stateData = useSelector(
    (state: DefaultState) => state.manualUploads.data
  )
  const values = useWatch({ control })

  const onSearch = async (e) => {
    e.preventDefault
    const { startdate, enddate, startsector, endsector, collectionIds } =
      getValues()
    const start = startdate
        ? new Date(startdate).valueOf()
        : startsector
        ? startsector
        : null,
      end = enddate
        ? new Date(enddate).valueOf()
        : endsector
        ? endsector
        : null,
      fbMask =
        collectionIds.length ? collectionIds.length > 0
          ? collectionIds.reduce((a, b) => Number(a) + Number(b))
          : 65535 : 65535
    const deviceId = stateData.deviceId
    dispatch(postManualUploads({ type, start, end, fbMask, deviceId }))
  }

  useEffect(() => {
    if (!Object.values(stateData).some((value) => value === null || value === undefined)) {
      if(stateData.vehicleId && stateData.fleetId){
        window.location.href = `/ui/#!/fleet/${stateData.fleetId}/vehicle/${stateData.vehicleId}`
      } else {
        window.location.href = `/ui/#!/`
      }
    }
  }, [...Object.keys(stateData).map((key) => stateData[key])])

  const handleSelect = (e) => {
    setType(e.target.value)
    setValue('startsector', undefined)
    setValue('endsector', undefined)
    setValue('startdate', undefined)
    setValue('enddate', undefined)
    setValue('collectionIds', [65535])
    clearErrors()
  }

  useEffect(() => {
    const checkValid = schema.validate(values)
    if (checkValid.error != undefined) {
      setVisible(true)
    } else {
      setVisible(false)
    }

    if (type == 'time'){
      if ((values.startdate == values.enddate) || checkValid.error != undefined || (values.startdate == undefined || values.enddate == undefined)) {
        setDisable(true)
      } else {
        setDisable(false)
      }
    }
    
    if (type == 'sector') {
      if ((values.startsector == values.endsector) || checkValid.error != undefined || (values.startsector == undefined || values.endsector == undefined)) {
        setDisable(true)
      } else {
        setDisable(false)
      }
    }
  }, [values])
  
  return (
    <>
      <div id='manual_uploads_form_row'>
        <Col id='manual_uploads_form_container'>
          <Form
            className='manual-uploads-form'
            onSubmit={handleSubmit(onSearch)}
          >
            <FormFeedback
              valid={errors ? false : true}
              role='alert'
              style={{ display: 'block' }}
            >
              {errors.message}
            </FormFeedback>
            <Row className='manual-uploads-row type-input-row'>
              <Col id='manual_uploads_type_col'>
                <Label for='type_input' className='manual-uploads-label'>
                  Type
                </Label>
                <Input
                  id='type_input'
                  name='type'
                  type='select'
                  value={type}
                  className='manual-uploads-input'
                  onChange={(e) => {e.preventDefault(); handleSelect(e)}}
                >
                  {searchTypes.map((type, index) => {
                    return (
                      <option key={index} value={type.value}>
                        {type.name}
                      </option>
                    )
                  })}
                </Input>
              </Col>
            </Row>
            {type == 'time' && (
              <>
                <Row className='manual-uploads-row date-time-row'>
                  <ManualUploadsDateTime
                    name='startdate'
                    label='Start Date'
                    required={true}
                    errors={errors}
                    control={control}
                    visible={visible}
                  />
                  <ManualUploadsDateTime
                    name='enddate'
                    label='End Date'
                    required={false}
                    errors={errors}
                    control={control}
                    visible={visible}
                  />
                </Row>
              </>
            )}
            {type == 'sector' && (
              <>
                <Row className='manual-uploads-row sector-input-row'>
                  <ManualUploadsSector
                    name='startsector'
                    label='Start Sector'
                    required={true}
                    errors={errors}
                    control={control}
                    visible={visible}
                  />
                  <ManualUploadsSector
                    name='endsector'
                    label='End Sector'
                    required={false}
                    errors={errors}
                    control={control}
                    visible={visible}
                  />
                </Row>

              </>
            )}
            <Row className='manual-uploads-ids-input-row'>
                  <Col className='manual-uploads-ids-input'>
                    <ManualUploadsCollectionIds
                      name='collectionIds'
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      visible={visible}
                    />
                  </Col>
              </Row>    
            <Row className='manual-uploads-row button-row'>
              <Button
                onClick={() => window.history.back()}
                id='manual_uploads_form_submit'
                style={{
                  backgroundColor: '#5cb85c',
                  color: '#fff',
                  fontFamily: 'Helvetica Neue helvetica, verdana, sans-serif',
                  fontSize: '14px'
                }}
              >
                Back
              </Button>
              <Button
                disabled={disable}
                onClick={onSearch}
                id='manual_uploads_form_submit'
                style={{
                  backgroundColor: '#337ab7',
                  color: '#fff',
                  fontFamily: 'Helvetica Neue helvetica, verdana, sans-serif',
                  fontSize: '14px'
                }}
              >
                Submit
              </Button>
            </Row>
          </Form>
        </Col>
      </div>
    </>
  )
}

export default ManualUploadsUserForm
